import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Control, Controller, FieldErrors, UseFormSetValue, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { useGetFacilitiesQuery } from '@/services/faciltiesApi'
import { cn } from '@/lib/utils'
import { formatToPKR } from '@/lib/helper'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { Event, EventAddOn } from '@/types/event'
import { Badge } from '@/components/ui/badge'
import { Plus, Minus, Trash2 } from 'lucide-react'

interface EventAddOnProps {
  control: Control<Event>;
  errors: FieldErrors<Event>;
  watch: UseFormWatch<Event>;
  setValue: UseFormSetValue<Event>;
  trigger?: UseFormTrigger<Event>;
  action: 'add' | 'edit';
  open: boolean;
}

const EventAddOns = ({ control, errors, watch, setValue, trigger, action, open }: EventAddOnProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const facilityName = watch("facility_name");
  const eventAddOns = watch("event_add_ons") || [];

  // Fetch facilities for mock data since event APIs aren't ready
  const { data: facilitiesData, isLoading } = useGetFacilitiesQuery({
    query: "is_add_on=true",
    orgId,
  });

  // Mock event add-ons data since APIs aren't ready
  const mockEventAddOns = useMemo(() => {
    if (!facilitiesData?.data) return [];

    return facilitiesData.data.slice(0, 3).map((facility, index) => ({
      id: `addon_${index + 1}`,
      name: `${facility.name} Access`,
      description: `Additional access to ${facility.name} facilities during the event`,
      price: (index + 1) * 500, // Mock pricing
      type: index === 0 ? 'inclusive' : 'exclusive',
      max_attendees: facility.max_capacity || 50,
      facility_id: facility.id,
    }));
  }, [facilitiesData]);

  const availableAddOns = mockEventAddOns;

  const handleAddAddOn = (addOn: typeof availableAddOns[0]) => {
    const newAddOn: EventAddOn = {
      id: addOn.id,
      name: addOn.name,
      price: addOn.price,
      type: addOn.type as 'inclusive' | 'exclusive',
      max_attendees: 1,
      description: addOn.description,
      facility_id: addOn.facility_id,
    };

    const updatedAddOns = [...eventAddOns, newAddOn];
    setValue("event_add_ons", updatedAddOns);
    if (trigger) {
      trigger("event_add_ons");
    }
  };

  const handleRemoveAddOn = (addOnIdToRemove: string) => {
    const updatedAddOns = eventAddOns.filter((addOn: { id: string }) => addOn.id !== addOnIdToRemove);
    setValue("event_add_ons", updatedAddOns);
    if (trigger) {
      trigger("event_add_ons");
    }
  };

  const isAddOnSelected = (addOnId: string) => {
    return eventAddOns.some((a: { id: string }) => a.id === addOnId);
  };

  const getSelectedAddOnIndex = (addOnId: string) => {
    return eventAddOns.findIndex((a: { id: string }) => a.id === addOnId);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading add-ons...</div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <p className="font-medium text-base">Event Add-Ons</p>
          <div className="text-sm text-muted-foreground">
            {availableAddOns.length} add-on(s) available
          </div>
        </div>
        <Separator className="h-[1px] my-4" />

        {availableAddOns.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-muted-foreground mb-2">
              <i className="fa fa-info-circle text-4xl text-gray-300"></i>
            </div>
            <p className="text-sm text-muted-foreground">
              No add-ons available for events
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              You can still proceed to the next step
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected Add-Ons */}
            {eventAddOns.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700">Selected Add-Ons</h4>
                {eventAddOns.map((addOn: any, index: number) => (
                  <Card key={addOn.id} className="p-4 bg-primary/5 border-primary/20">
                    <CardContent className="p-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full bg-primary"></div>
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{addOn.name}</span>
                            <Badge variant="outline" className="w-fit mt-1">
                              {addOn.type === 'inclusive' ? 'Included' : 'Optional'}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAddOn(addOn.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">Price</Label>
                          <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                            {formatToPKR(addOn.price || 0)}
                          </div>
                          <span className="text-xs text-muted-foreground">Read-only</span>
                        </div>

                        {addOn.type === 'exclusive' && (
                          <div className="space-y-1">
                            <Label className="text-sm font-medium">
                              Max Attendees <span className="text-red-500">*</span>
                            </Label>
                            <Controller
                              name={`event_add_ons.${index}.max_attendees`}
                              control={control}
                              defaultValue={1}
                              rules={{
                                required: "Required",
                                min: { value: 1, message: "Must be at least 1" },
                                validate: (value) => {
                                  if (value && value > addOn.max_attendees) {
                                    return `Max Attendees cannot exceed ${addOn.max_attendees}`;
                                  }
                                  return true;
                                }
                              }}
                              render={({ field }) => (
                                <Input
                                  type="number"
                                  placeholder="Enter max attendees"
                                  {...field}
                                  className="h-10"
                                />
                              )}
                            />
                            {errors?.event_add_ons?.[index]?.max_attendees && (
                              <span className="text-red-500 text-xs">
                                {errors.event_add_ons?.[index]?.max_attendees?.message}
                              </span>
                            )}
                          </div>
                        )}

                        {addOn.description && (
                          <div className="space-y-1 col-span-full">
                            <Label className="text-sm font-medium">Description</Label>
                            <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                              {addOn.description}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Available Add-Ons */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700">Available Add-Ons</h4>
              {availableAddOns.map((addOn, index) => {
                const selected = isAddOnSelected(addOn.id);
                const canAdd = addOn.type === 'exclusive' || !selected;

                return (
                  <Card
                    key={addOn.id}
                    className={cn(
                      "transition-all duration-200",
                      selected
                        ? "border-primary/50 bg-primary/5"
                        : "border-gray-200 bg-gray-50 opacity-70"
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-3 h-3 rounded-full",
                            selected ? "bg-primary" : "bg-gray-300"
                          )}></div>
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{addOn.name}</span>
                            <Badge variant="outline" className="w-fit mt-1">
                              {addOn.type === 'inclusive' ? 'Included' : 'Optional'}
                            </Badge>
                          </div>
                        </div>
                        {canAdd && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddAddOn(addOn)}
                            disabled={selected}
                            className="h-8"
                          >
                            {selected ? 'Added' : 'Add'}
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-sm font-medium">Price</Label>
                          <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                            {formatToPKR(addOn.price || 0)}
                          </div>
                          <span className="text-xs text-muted-foreground">Read-only</span>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-sm font-medium">Max Capacity</Label>
                          <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                            {addOn.max_attendees} attendees
                          </div>
                          <span className="text-xs text-muted-foreground">Read-only</span>
                        </div>

                        {addOn.description && (
                          <div className="space-y-1 col-span-full">
                            <Label className="text-sm font-medium">Description</Label>
                            <div className="p-2 bg-gray-100 rounded border text-sm text-gray-700">
                              {addOn.description}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Summary */}
            {eventAddOns.length > 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">
                    Total Selected Add-Ons: {eventAddOns.length}
                  </span>
                  <span className="text-sm text-blue-700">
                    Total Value: {formatToPKR(eventAddOns.reduce((sum: number, addOn: any) => sum + (addOn.price || 0), 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventAddOns;
